import { ActionTree } from 'vuex';
import { RootState, SingleFilter } from '@/store/types';
import api from '@/api';
import * as articleRequests from '@/api/requests/article/articles';
import * as pricelistRequests from '@/api/requests/article/pricelists';
import * as warehouseRequests from '@/api/requests/article/warehouses';
import * as categoryRequests from '@/api/requests/article/categories';
import { ArticleResource, PriceListResource, WarehouseResource, CategoryResource } from '@/api/requests/article/types';
import { ArticleState } from './types';
import Article from '@/api/models/article';

export const actions: ActionTree<ArticleState, RootState> = {

    /** Articles */

    /**
     * Fetch all articles.
     *
     * @param commit
     * @param getters
     */
    async fetchArticles({ commit, getters }) {
        const response = await api.call(articleRequests.ALL(getters.httpFilters));
        commit('setArticles', response.data.data);
    },

    /**
     * Create a new article.
     *
     * @param commit
     * @param article
     */
    async createArticle({ commit }, article: Article) {
        const response = await api.call(articleRequests.STORE(article));
        const resource: ArticleResource = response.data.data;

        commit('addArticle', resource);
    },

    /**
     * Delete article by id.
     *
     * @param commit
     * @param articleIds
     */
    async deleteArticle({ commit }, ...articleIds: number[]) {
        await Promise.all(articleIds.map(async (articleId) => {
            await api.call(articleRequests.DELETE(articleId));
            commit('removeArticle', articleId);
        }));
    },

    /**
     * Update an existing article.
     *
     * @param commit
     * @param article
     */
    async updateArticle({ commit }, article: Article) {
        if (!article.id) {
            throw new Error('No article id provided.');
        }
        const response = await api.call(articleRequests.UPDATE(article.id, article));
        const resource: ArticleResource = response.data.data;

        commit('updateArticle', resource);
    },

    /**
     * Set active article.
     *
     * @param commit
     * @param getters
     * @param articleId
     */
    async setActiveArticle({ commit, getters }, articleId: number) {
        if (!getters.isArticleLoaded(articleId)) {
            const response = await api.call(articleRequests.FIND(articleId));
            commit('upsertArticle', response.data.data);
        }
        commit('setActiveArticleId', articleId);
    },

    /** Price lists */

    /**
     * Fetch all price lists.
     *
     * @param commit
     * @param getters
     */
    async fetchPriceLists({ commit, getters }) {
        const response = await api.call(pricelistRequests.ALL(getters.httpFilters));
        commit('setPriceLists', response.data.data);
    },

    /**
     * Create a new price list.
     *
     * @param commit
     * @param priceList
     */
    async createPriceList({ commit }, priceList: PriceListResource) {
        const response = await api.call(pricelistRequests.STORE(priceList));
        const resource: PriceListResource = response.data.data;

        commit('addPriceList', resource);
    },

    /**
     * Delete price list by id.
     *
     * @param commit
     * @param priceListIds
     */
    async deletePriceList({ commit }, ...priceListIds: number[]) {
        await Promise.all(priceListIds.map(async (priceListId) => {
            await api.call(pricelistRequests.DELETE(priceListId));
            commit('removePriceList', priceListId);
        }));
    },

    /**
     * Update an existing price list.
     *
     * @param commit
     * @param priceList
     */
    async updatePriceList({ commit }, priceList: PriceListResource) {
        if (!priceList.id) {
            throw new Error('No price list id provided.');
        }
        const response = await api.call(pricelistRequests.UPDATE(priceList.id, priceList));
        const resource: PriceListResource = response.data.data;

        commit('updatePriceList', resource);
    },

    /**
     * Set active price list.
     *
     * @param commit
     * @param getters
     * @param priceListId
     */
    async setActivePriceList({ commit, getters }, priceListId: number) {
        const response = await api.call(pricelistRequests.FIND(priceListId));
        commit('upsertPriceList', response.data.data);
        commit('setActivePriceListId', priceListId);
    },


    /**
     * Fetch all price lists.
     *
     * @param commit
     * @param getters
     */
    async fetchArticlesPriceList({ commit, getters }, priceListId: number) {
        const response = await api.call(pricelistRequests.ALLARTICLES(priceListId, getters.httpFilters));
        const priceList = getters.getPriceListById(priceListId);
        commit('setArticlesPriceList', {priceList, articles: response.data.data});
    },

    /** Warehouses */

    /**
     * Fetch all warehouses.
     *
     * @param commit
     * @param getters
     */
    async fetchWarehouses({ commit, getters }) {
        const response = await api.call(warehouseRequests.ALL(getters.httpFilters));
        commit('setWarehouses', response.data.data);
    },

    /**
     * Create a new warehouse.
     *
     * @param commit
     * @param warehouse
     */
    async createWarehouse({ commit }, warehouse: WarehouseResource) {
        const response = await api.call(warehouseRequests.STORE(warehouse));
        const resource: WarehouseResource = response.data.data;

        commit('addWarehouse', resource);
    },

    /**
     * Delete warehouse by id.
     *
     * @param commit
     * @param warehouseIds
     */
    async deleteWarehouse({ commit }, ...warehouseIds: number[]) {
        await Promise.all(warehouseIds.map(async (warehouseId) => {
            await api.call(warehouseRequests.DELETE(warehouseId));
            commit('removeWarehouse', warehouseId);
        }));
    },

    /**
     * Update an existing warehouse.
     *
     * @param commit
     * @param warehouse
     */
    async updateWarehouse({ commit }, warehouse: WarehouseResource) {
        if (!warehouse.id) {
            throw new Error('No warehouse id provided.');
        }
        const response = await api.call(warehouseRequests.UPDATE(warehouse.id, warehouse));
        const resource: WarehouseResource = response.data.data;

        commit('updateWarehouse', resource);
    },

    /** Categories */

    /**
     * Fetch all categories.
     *
     * @param commit
     * @param getters
     */
    async fetchCategories({ commit, getters }) {
        const response = await api.call(categoryRequests.ALL(getters.httpFilters));
        commit('setCategories', response.data.data);
    },

    /**
     * Create a new category.
     *
     * @param commit
     * @param category
     */
    async createCategory({ commit }, category: CategoryResource) {
        const response = await api.call(categoryRequests.STORE(category));
        const resource: CategoryResource = response.data.data;

        commit('addCategory', resource);
    },

    /**
     * Delete category by id.
     *
     * @param commit
     * @param categoryIds
     */
    async deleteCategory({ commit }, ...categoryIds: number[]) {
        await Promise.all(categoryIds.map(async (categoryId) => {
            await api.call(categoryRequests.DELETE(categoryId));
            commit('removeCategory', categoryId);
        }));
    },

    /**
     * Update an existing category.
     *
     * @param commit
     * @param category
     */
    async updateCategory({ commit }, category: CategoryResource) {
        if (!category.id) {
            throw new Error('No category id provided.');
        }
        const response = await api.call(categoryRequests.UPDATE(category.id, category));
        const resource: CategoryResource = response.data.data;

        commit('updateCategory', resource);
    },

    /** Filters */

    /**
     * Reset a single filter.
     *
     * @param commit
     * @param key
     */
    resetFilter({ commit }, key: string) {
        commit('resetFilter', key);
    },


    /**
     * Set filters.
     *
     * @param commit
     * @param dispatch
     * @param filters
     */
    setFilters({ commit, dispatch }, filters: SingleFilter[]) {
        filters.map((filter) => commit('setFilter', filter));
    },

};
